import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import DOMPurify from "dompurify";
import { RendererProps } from "../FilePreviewModal";
import CustomScrollbars from "../../CustomScrollbars";
import SearchBar from "../SearchBar";
import { escapeRegex, SEARCH_MARK_CLASS } from "../searchUtils";
import { useDomSearchNavigation } from "../useDomSearchNavigation";

const DocxRenderer: React.FC<RendererProps> = ({ blob, fileName }) => {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasFormattingLoss, setHasFormattingLoss] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightTick, setHighlightTick] = useState(0);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!blob) return;
    const fileBlob = blob;
    let cancelled = false;
    const run = async () => {
      try {
        const arrayBuffer = await fileBlob.arrayBuffer();
        if (cancelled) return;
        const mammoth = await import("mammoth/mammoth.browser");
        if (cancelled) return;
        const result = await mammoth.convertToHtml({ arrayBuffer });
        if (cancelled) return;
        const clean = DOMPurify.sanitize(result.value, {
          USE_PROFILES: { html: true },
        });
        setHtml(clean);
        const hasWarn =
          Array.isArray(result.messages) &&
          result.messages.some(
            (m: { type?: string }) => m.type === "warning",
          );
        setHasFormattingLoss(hasWarn);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : `Failed to render "${fileName}"`,
        );
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [blob, fileName]);

  useEffect(() => {
    const node = contentRef.current;
    if (!node || html == null) return;

    const oldMarks = node.querySelectorAll(`mark.${SEARCH_MARK_CLASS}`);
    oldMarks.forEach((m) => {
      const parent = m.parentNode;
      if (!parent) return;
      const text = document.createTextNode(m.textContent || "");
      parent.replaceChild(text, m);
    });
    node.normalize();

    if (!search) {
      setHighlightTick((t) => t + 1);
      return;
    }

    const re = new RegExp(escapeRegex(search), "gi");

    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => {
        const parentEl = (n as Text).parentElement;
        if (!parentEl) return NodeFilter.FILTER_REJECT;
        const tag = parentEl.tagName;
        if (tag === "SCRIPT" || tag === "STYLE") {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    let cur: Node | null;
    while ((cur = walker.nextNode())) {
      textNodes.push(cur as Text);
    }

    for (const tn of textNodes) {
      const txt = tn.nodeValue ?? "";
      re.lastIndex = 0;
      if (!re.test(txt)) continue;
      re.lastIndex = 0;

      const frag = document.createDocumentFragment();
      let lastIdx = 0;
      let m: RegExpExecArray | null;
      while ((m = re.exec(txt)) !== null) {
        if (m.index > lastIdx) {
          frag.appendChild(
            document.createTextNode(txt.slice(lastIdx, m.index)),
          );
        }
        const mark = document.createElement("mark");
        mark.className = SEARCH_MARK_CLASS;
        mark.textContent = m[0];
        frag.appendChild(mark);
        lastIdx = m.index + m[0].length;
        if (m.index === re.lastIndex) re.lastIndex += 1;
      }
      if (lastIdx < txt.length) {
        frag.appendChild(document.createTextNode(txt.slice(lastIdx)));
      }
      tn.parentNode?.replaceChild(frag, tn);
    }

    setHighlightTick((t) => t + 1);
  }, [html, search]);

  const { total, current, gotoNext, gotoPrev } = useDomSearchNavigation(
    contentRef,
    [highlightTick],
  );

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (html == null) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          p: 4,
        }}
      >
        <CircularProgress size={28} />
        <Box sx={{ fontSize: 13, color: "text.secondary" }}>
          Rendering document…
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          flexShrink: 0,
        }}
      >
        <SearchBar
          value={search}
          onChange={setSearch}
          matchCount={total}
          currentMatch={current}
          onPrev={gotoPrev}
          onNext={gotoNext}
        />
      </Box>
      {hasFormattingLoss && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          Some formatting may be lost
        </Alert>
      )}
      <CustomScrollbars sx={{ flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            py: 3,
            backgroundColor: (t) => t.palette.grey[100],
            minHeight: "100%",
          }}
        >
          <Box
            ref={contentRef}
            sx={{
              backgroundColor: "#fff",
              mx: "auto",
              maxWidth: 820,
              px: { xs: 3, sm: 6 },
              py: { xs: 3, sm: 5 },
              boxShadow: 1,
              borderRadius: 0.5,
              "& p": { my: 1, lineHeight: 1.6 },
              "& h1, & h2, & h3, & h4, & h5, & h6": { mt: 2, mb: 1 },
              "& table": {
                borderCollapse: "collapse",
                my: 2,
                maxWidth: "100%",
              },
              "& td, & th": {
                border: "1px solid #ccc",
                p: 0.75,
                verticalAlign: "top",
              },
              "& img": { maxWidth: "100%", height: "auto" },
              "& ul, & ol": { pl: 3, my: 1 },
              "& a": { color: "primary.main" },
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </Box>
      </CustomScrollbars>
    </Box>
  );
};

export default DocxRenderer;
